import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FeaturesDictionary, SectionsByRow } from "@src/store/auto-pricer/autopricerSlice";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { FillLayer, MapboxMap } from "react-map-gl";
import {
	createFillLayer,
	groupFeaturesBySection,
	normalizeName,
	queryMapPolygonFeatures,
} from "@src/components/map-view/map.utils";
import { RootState } from "@src/store/store";
import { SELECTED_SECTION_FILL_PAINT, USERS_SECTION_FILL_PAINT } from "@src/components/map-view/map.constants";
import { difference, getUniqueArrayValues, convertMapToRecord, mapDictionaryKeysToValues, union } from "@src/utils/array.utils";
import { applyFilterLogic } from "@src/utils/mapFilter.utils";
import { getSetting, saveSetting } from "@src/utils/storage.utils";
import {
	isGeneralAdmissionQuery,
	matchesFilterTokens,
	matchesSectionFilterTokens,
	parseFilterTokens,
} from "@src/utils/sectionFilter.utils";

const toMapSectionKey = (name: string) => normalizeName((name ?? "").replace(/"/g, ""));

export interface MapSliceType {
	[key: string]: string | Array<string> | FeaturesDictionary | SectionsByRow | boolean | number | null;

	featuresBySection: FeaturesDictionary;
	sectionsByRow: SectionsByRow;
	selectedSectionNames: Array<string>;
	selectedRowNames: Array<string>;
	usersSectionNames: Array<string>;
	selectedMinQuantity: string;
	lockFilters: boolean;
	autoApplyFilters: boolean;
	section: string;
	row: string;
	lastSearchSection: string;
	lastSearchRow: string;
	sectionStartRange: string;
	sectionEndRange: string;
	rowStartRange: string;
	rowEndRange: string;
	minQuantity: string;
	selectionEventId: number | null;
	filtersInitializedEventId: number | null;
	autoAppliedSectionNames: Array<string>;
}

const initialState: MapSliceType = {
	featuresBySection: {},
	sectionsByRow: {},
	selectedSectionNames: [],
	selectedRowNames: [],
	usersSectionNames: [],
	selectedMinQuantity: "",
	lockFilters: JSON.parse(getSetting("portalMapLockFilters", "false")),
	autoApplyFilters: false,
	section: "",
	row: "",
	lastSearchSection: "",
	lastSearchRow: "",
	sectionStartRange: "",
	sectionEndRange: "",
	rowStartRange: "",
	rowEndRange: "",
	minQuantity: "",
	selectionEventId: null,
	filtersInitializedEventId: null,
	autoAppliedSectionNames: [],
};

export interface InitializeStatePayload {
	eventInventoryData: Array<EventInventoryRecord>;
	map: MapboxMap;
}

const mapSlice = createSlice({
	name: "map",
	initialState,
	reducers: {
		initializeState: (state, action: PayloadAction<InitializeStatePayload>) => {
			const { eventInventoryData, map } = action.payload;

			const allMapboxSectionFeatures = queryMapPolygonFeatures(undefined, map, "section");
			const allMapboxRowFeatures = queryMapPolygonFeatures(undefined, map, "row");

			const sectionsByRow = new Map<string, Array<string>>();

			allMapboxRowFeatures.forEach((mapboxRowFeature) => {
				const rowName = normalizeName(mapboxRowFeature.properties.name ?? "");
				const sectionName = normalizeName(mapboxRowFeature.properties.parent_name ?? "");

				if (!rowName || !sectionName) return;

				sectionsByRow.set(rowName, union(sectionsByRow.get(rowName) ?? [], sectionName));
			});

			state.featuresBySection = convertMapToRecord(groupFeaturesBySection(allMapboxSectionFeatures));
			state.sectionsByRow = convertMapToRecord(sectionsByRow);
			state.usersSectionNames = getUniqueArrayValues(eventInventoryData.map(({ Section }) => normalizeName(Section)));
		},
		initializeMapFilters: (state, action: PayloadAction<{ eventId?: number | null } | undefined>) => {
			const eventId = action.payload?.eventId;
			if (eventId !== undefined) {
				if (eventId === state.filtersInitializedEventId) return;
				state.filtersInitializedEventId = eventId;
			}

			if (state.lockFilters) return;

			const savedFilters = localStorage.getItem("portalMapFilters");
			if (!savedFilters) return;

			try {
				const parsed = JSON.parse(savedFilters);

				state.sectionStartRange = parsed.sectionStartRange ?? "";
				state.sectionEndRange = parsed.sectionEndRange ?? "";
				state.rowStartRange = parsed.rowStartRange ?? "";
				state.rowEndRange = parsed.rowEndRange ?? "";
				state.minQuantity = parsed.minQuantity ?? "";
				state.lockFilters = parsed.lockFilters ?? state.lockFilters;
				state.section = parsed.section ?? "";
				state.row = parsed.row ?? "";

				const applied = applyFilterLogic(parsed);
				state.selectedSectionNames = applied.selectedSectionNames;
				state.autoAppliedSectionNames = [];
				state.selectedRowNames = applied.selectedRowNames;
				state.selectedMinQuantity = applied.selectedMinQuantity;
			} catch (e) {
				console.warn("Invalid portalMapFilters in localStorage", e);
			}
		},
		toggleSection: (state, action: PayloadAction<string>) => {
			const { selectedSectionNames } = state;
			const sectionName = action.payload;

			state.selectedSectionNames = selectedSectionNames.includes(sectionName)
				? difference(selectedSectionNames, sectionName)
				: union(selectedSectionNames, sectionName);
			state.autoAppliedSectionNames = difference(state.autoAppliedSectionNames, sectionName);
		},
		addSection: (state, action: PayloadAction<string>) => {
			const { selectedSectionNames } = state;
			const sectionName = action.payload;

			if (selectedSectionNames.includes(sectionName)) return;

			state.selectedSectionNames = [...selectedSectionNames, sectionName];
		},
		setFilters: (state, action: PayloadAction<{ name: string; value: string }>) => {
			const { name, value } = action.payload;

			state[name] = value;
		},
		applyFilters: (
			state,
			action: PayloadAction<{ data?: Partial<MapSliceType>; type?: "fromFilters" | "fromInventoryTable" }>,
		) => {
			if (state.lockFilters) return;

			const incoming = action.payload?.data;

			if (incoming && state.autoApplyFilters) {
				if ("section" in incoming) state.section = incoming.section ?? "";
				if ("sectionStartRange" in incoming) state.sectionStartRange = incoming.sectionStartRange ?? "";
				if ("sectionEndRange" in incoming) state.sectionEndRange = incoming.sectionEndRange ?? "";
				if ("row" in incoming) state.row = incoming.row ?? "";
				if ("rowStartRange" in incoming) state.rowStartRange = incoming.rowStartRange ?? "";
				if ("rowEndRange" in incoming) state.rowEndRange = incoming.rowEndRange ?? "";
				if ("minQuantity" in incoming) state.minQuantity = incoming.minQuantity ?? "";
			}

			const filterState = {
				section: state.section,
				sectionStartRange: state.sectionStartRange,
				sectionEndRange: state.sectionEndRange,
				row: state.row,
				rowStartRange: state.rowStartRange,
				rowEndRange: state.rowEndRange,
				minQuantity: state.minQuantity,
			};

			const applied = applyFilterLogic(filterState);

			if (action.payload.type !== "fromInventoryTable") {
				state.selectedSectionNames = applied.selectedSectionNames;
				state.autoAppliedSectionNames = [];
			}

			state.selectedRowNames = applied.selectedRowNames;
			state.selectedMinQuantity = applied.selectedMinQuantity;

			saveSetting("portalMapFilters", JSON.stringify(filterState), true);
		},
		applySearchFilters: (state, action: PayloadAction<{ section: string; row: string }>) => {
			if (state.lockFilters) return;

			const { section, row } = action.payload;

			const hasSectionChanged = state.lastSearchSection !== section;
			const hasRowChanged = state.lastSearchRow !== row;
			if (!hasSectionChanged && !hasRowChanged) return;

			state.lastSearchSection = section;
			state.lastSearchRow = row;

			if (hasSectionChanged) {
				state.section = section;

				if (section) {
					state.sectionStartRange = "";
					state.sectionEndRange = "";
				}
			}

			if (hasRowChanged) {
				state.row = row;

				if (row) {
					state.rowStartRange = "";
					state.rowEndRange = "";
				}
			}

			const filterState = {
				section: state.section,
				sectionStartRange: state.sectionStartRange,
				sectionEndRange: state.sectionEndRange,
				row: state.row,
				rowStartRange: state.rowStartRange,
				rowEndRange: state.rowEndRange,
				minQuantity: state.minQuantity,
			};

			const applied = applyFilterLogic(filterState);

			state.selectedSectionNames = applied.selectedSectionNames;
			state.autoAppliedSectionNames = [];
			state.selectedRowNames = applied.selectedRowNames;
			state.selectedMinQuantity = applied.selectedMinQuantity;

			saveSetting("portalMapFilters", JSON.stringify(filterState), true);
		},
		applySelectedListings: (state, action: PayloadAction<{ sections: Array<string> }>) => {
			if (state.lockFilters) return;

			const listingSections = getUniqueArrayValues(action.payload.sections.map((s) => normalizeName(s)).filter(Boolean));
			const previousAuto = state.autoAppliedSectionNames;
			const keptAuto = previousAuto.filter((section) => listingSections.includes(section));
			const remaining = state.selectedSectionNames.filter(
				(section) => !previousAuto.includes(section) || listingSections.includes(section),
			);
			const added = listingSections.filter((section) => !remaining.includes(section));

			state.selectedSectionNames = [...remaining, ...added];
			state.autoAppliedSectionNames = [...keptAuto, ...added];
		},
		resetSelectedSections: (state) => {
			state.selectedSectionNames = [];
			state.autoAppliedSectionNames = [];
		},
		resetMapState: (state, action: PayloadAction<{ isInitial?: boolean; eventId?: number | null }>) => {
			const { eventId } = action.payload;
			if (eventId !== undefined) {
				const isSameEvent = eventId !== null && eventId === state.selectionEventId;
				state.selectionEventId = eventId;
				if (isSameEvent) return;
			}

			if (state.lockFilters) return;

			state.selectedSectionNames = [];
			state.autoAppliedSectionNames = [];
			state.selectedRowNames = [];
			state.selectedMinQuantity = "";
			state.section = "";
			state.row = "";
			state.lastSearchSection = "";
			state.lastSearchRow = "";
			state.sectionStartRange = "";
			state.sectionEndRange = "";
			state.rowStartRange = "";
			state.rowEndRange = "";
			state.minQuantity = "";

			if (!action.payload.isInitial) {
				saveSetting("portalMapFilters", null, true);
			}
		},
		toggleLockFilters: (state) => {
			const payload = !state.lockFilters;
			saveSetting("portalMapLockFilters", payload, true);
			state.lockFilters = payload;
		},
		setAutoApplyFilters: (state, action: PayloadAction<boolean>) => {
			state.autoApplyFilters = action.payload;
		},
	},
});

export const _selectSelectedSectionNames = (state: RootState) => state.map.selectedSectionNames;
export const _selectSelectedRowNames = (state: RootState) => state.map.selectedRowNames;
export const _selectMapSplitMinQuantity = (state: RootState) => state.map.selectedMinQuantity;
const _selectUsersSectionNames = (state: RootState) => state.map.usersSectionNames;
const _selectFeaturesBySection = (state: RootState) => state.map.featuresBySection;

const _selectSectionFilterText = (state: RootState) => state.map.section;
const _selectSectionsByRow = (state: RootState) => state.map.sectionsByRow;
const _selectMarketplaceListings = (state: RootState) => state.marketplace.seatgeek.activeListings.data.Listings;
const _selectEventInventory = (state: RootState) => state.pricing.eventInventoryAll.data;

export interface RowSectionPair {
	section: string;
	row: string;
}

const collectRowMatchedSectionKeys = (
	selectedRowNames: Array<string>,
	sectionsByRow: SectionsByRow,
	listings: Array<RowSectionPair>,
) => {
	const rowMatchedKeys = new Set<string>();

	Object.entries(sectionsByRow).forEach(([rowName, sectionNames]) => {
		if (!matchesFilterTokens(selectedRowNames, rowName)) return;
		sectionNames.forEach((sectionName) => rowMatchedKeys.add(toMapSectionKey(sectionName)));
	});

	listings.forEach(({ section, row }) => {
		if (!matchesFilterTokens(selectedRowNames, row ?? "")) return;
		rowMatchedKeys.add(toMapSectionKey(section ?? ""));
	});

	rowMatchedKeys.delete("");

	return rowMatchedKeys;
};

export const resolveMatchedSectionKeys = (
	sectionKeys: Array<string>,
	selectedRowNames: Array<string>,
	sectionsByRow: SectionsByRow,
	listings: Array<RowSectionPair> = [],
) => {
	if (selectedRowNames.length === 0) return sectionKeys;

	const rowMatchedKeys = collectRowMatchedSectionKeys(selectedRowNames, sectionsByRow, listings);

	return sectionKeys.length > 0 ? sectionKeys.filter((key) => rowMatchedKeys.has(key)) : Array.from(rowMatchedKeys);
};

export const selectPricingMapFillLayers = createSelector(
	[
		_selectSelectedSectionNames,
		_selectUsersSectionNames,
		_selectFeaturesBySection,
		_selectSectionFilterText,
		_selectSelectedRowNames,
		_selectSectionsByRow,
		_selectMarketplaceListings,
		_selectEventInventory,
	],
	(
		selectedSectionNames,
		usersSectionNames,
		featuresBySection,
		sectionFilterText,
		selectedRowNames,
		sectionsByRow,
		marketplaceListings,
		eventInventory,
	) => {
		const normalizedSelectedNames = getUniqueArrayValues(selectedSectionNames.map(toMapSectionKey));

		const generalAdmissionTokens = parseFilterTokens(sectionFilterText).filter(isGeneralAdmissionQuery);
		const generalAdmissionKeys = generalAdmissionTokens.length
			? Object.keys(featuresBySection).filter((key) => generalAdmissionTokens.some((token) => matchesSectionFilterTokens([token], key)))
			: [];

		const sectionKeys = getUniqueArrayValues([...normalizedSelectedNames, ...generalAdmissionKeys]);

		const rowSectionPairs: Array<RowSectionPair> = [
			...(marketplaceListings ?? []).map(({ s, r }) => ({ section: s, row: r })),
			...eventInventory.map(({ Section, Row }) => ({ section: Section ?? "", row: Row ?? "" })),
		];

		const matchedKeys = resolveMatchedSectionKeys(sectionKeys, selectedRowNames, sectionsByRow, rowSectionPairs);

		const selectedSectionsFeatures = mapDictionaryKeysToValues(featuresBySection, matchedKeys);
		const usersSectionsFeatures = mapDictionaryKeysToValues(featuresBySection, difference(usersSectionNames, matchedKeys));

		const fillLayers: Array<FillLayer> = [];

		if (selectedSectionsFeatures.length > 0) {
			fillLayers.push(createFillLayer("selected-sections-fill-layer", SELECTED_SECTION_FILL_PAINT, selectedSectionsFeatures));
		}

		if (usersSectionsFeatures.length > 0) {
			fillLayers.push(createFillLayer("users-sections-fill-layer", USERS_SECTION_FILL_PAINT, usersSectionsFeatures));
		}

		return fillLayers;
	},
);

export default mapSlice;
